/**
 * Knowledge Sources view — simplified description-based approach.
 * User describes what data source they want; AI figures out how to access it.
 */

import { html, nothing } from "lit";
import { t } from "../i18n.ts";
import type { KnowledgeSourceUI } from "../controllers/knowledge-sources.ts";

export type KnowledgeSourcesViewProps = {
  loading: boolean;
  error: string | null;
  sources: KnowledgeSourceUI[];
  editingSource: KnowledgeSourceUI | null;
  formOpen: boolean;
  collecting: string | null;
  testing: boolean;
  testResult: { ok: boolean; error?: string } | null;
  onRefresh: () => void;
  onAdd: () => void;
  onEdit: (source: KnowledgeSourceUI) => void;
  onDelete: (id: string) => void;
  onCollect: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => void;
  onFormClose: () => void;
  onFormSave: (data: Partial<KnowledgeSourceUI>) => void;
  onTestConnection: (data: Partial<KnowledgeSourceUI>) => void;
};

// ─── Main Render ─────────────────────────────────────────────

export function renderKnowledgeSources(props: KnowledgeSourcesViewProps) {
  return html`
    <div>
      <div class="row" style="justify-content: space-between; align-items: center; margin-bottom: 16px;">
        <h3 style="margin: 0; font-size: 18px; font-weight: 600;">${t("ks.title")}</h3>
        <div class="row" style="gap: 8px;">
          <button class="btn btn-sm" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? t("ks.loading") : t("ks.refresh")}
          </button>
          <button class="btn btn-sm primary" @click=${props.onAdd}>
            + ${t("ks.add_source")}
          </button>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger" style="margin-bottom: 12px;">${props.error}</div>` : nothing}

      ${
        props.sources.length === 0 && !props.loading
          ? renderEmptyState(props)
          : renderSourceList(props)
      }

      ${props.formOpen ? renderFormModal(props) : nothing}
    </div>
  `;
}

// ─── Empty State ─────────────────────────────────────────────

function renderEmptyState(props: KnowledgeSourcesViewProps) {
  return html`
    <div class="card" style="padding: 48px 40px; text-align: center;">
      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">🧠</div>
      <div style="font-size: 16px; font-weight: 600; margin-bottom: 8px;">${t("ks.empty_title")}</div>
      <div class="muted" style="margin-bottom: 28px; max-width: 420px; margin-left: auto; margin-right: auto; line-height: 1.6;">
        ${t("ks.empty_description")}
      </div>
      <button class="btn primary" style="padding: 10px 28px; font-size: 14px;" @click=${props.onAdd}>
        + ${t("ks.add_source")}
      </button>
    </div>
  `;
}

// ─── Source List ──────────────────────────────────────────────

function renderSourceList(props: KnowledgeSourcesViewProps) {
  return html`
    <div style="display: flex; flex-direction: column; gap: 10px;">
      ${props.sources.map((source) => renderSourceCard(source, props))}
    </div>
  `;
}

function renderSourceCard(source: KnowledgeSourceUI, props: KnowledgeSourcesViewProps) {
  const isCollecting = props.collecting === source.id || source.lastStatus === "collecting";
  const statusColor = getStatusColor(source.lastStatus);
  const displayText = source.instructions || source.name;

  return html`
    <div class="card source-card" style="padding: 16px;">
      <div class="row" style="justify-content: space-between; align-items: flex-start; gap: 12px;">
        <div style="flex: 1; min-width: 0;">
          <div class="row" style="align-items: center; gap: 8px; margin-bottom: 6px;">
            <span style="font-weight: 600; font-size: 15px;">${source.name}</span>
            <span class="pill source-status" style="--status-color: ${statusColor}">
              ${isCollecting ? "⟳ " : ""}${t(`ks.status_${source.lastStatus}`)}
            </span>
            ${
              !source.enabled
                ? html`<span class="pill" style="background: rgba(255,255,255,0.06); color: var(--text-muted);">${t("ks.disabled")}</span>`
                : nothing
            }
          </div>

          ${
            displayText !== source.name
              ? html`<div class="muted" style="font-size: 13px; margin-bottom: 6px; line-height: 1.5; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${displayText}</div>`
              : nothing
          }

          <div class="row" style="gap: 12px; flex-wrap: wrap; font-size: 12px;">
            <span class="muted">
              ${t("ks.schedule")}: ${formatSchedule(source.intervalMinutes, source.cronExpression)}
            </span>
            ${
              source.lastCollectedAt
                ? html`<span class="muted">${t("ks.last_collected")}: ${formatTimeAgo(source.lastCollectedAt)}</span>`
                : nothing
            }
            <span class="muted">${t("ks.docs_ingested")}: ${source.documentsIngested}</span>
            <span class="muted">${t("ks.runs")}: ${source.collectionCount}</span>
          </div>

          ${
            source.lastStatus === "error" && source.lastError
              ? html`<div style="margin-top: 6px; font-size: 12px; color: var(--danger, #ef4444); opacity: 0.9;">${source.lastError}</div>`
              : nothing
          }
        </div>

        <div class="row" style="gap: 6px; flex-shrink: 0;">
          <button
            class="btn btn-sm"
            ?disabled=${isCollecting}
            @click=${() => props.onCollect(source.id)}
            title=${t("ks.collect_now")}
          >
            ${isCollecting ? "⟳" : "▶"}
          </button>
          <button class="btn btn-sm" @click=${() => props.onEdit(source)} title=${t("ks.edit")}>
            ✎
          </button>
          <button
            class="btn btn-sm"
            @click=${() => props.onToggle(source.id, !source.enabled)}
            title=${source.enabled ? t("ks.disable") : t("ks.enable")}
          >
            ${source.enabled ? "⏸" : "▶"}
          </button>
          <button
            class="btn btn-sm danger"
            @click=${() => {
              if (confirm(t("ks.delete_confirm"))) {
                props.onDelete(source.id);
              }
            }}
            title=${t("ks.delete")}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  `;
}

// ─── Add/Edit Form Modal (Simplified) ────────────────────────

function renderFormModal(props: KnowledgeSourcesViewProps) {
  const isEdit = !!props.editingSource;
  const source = props.editingSource ?? ({} as Partial<KnowledgeSourceUI>);

  return html`
    <div class="source-form-overlay" @click=${(e: Event) => {
      if ((e.target as HTMLElement).classList.contains("source-form-overlay")) {
        props.onFormClose();
      }
    }}>
      <div class="card source-form" @click=${(e: Event) => e.stopPropagation()}>
        <div class="row" style="justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <h3 style="margin: 0; font-size: 17px; font-weight: 600;">
            ${isEdit ? t("ks.edit_source") : t("ks.add_source")}
          </h3>
          <button class="btn btn-sm" @click=${props.onFormClose}>✕</button>
        </div>

        <form
          @submit=${(e: Event) => {
            e.preventDefault();
            const form = e.target as HTMLFormElement;
            const fd = new FormData(form);
            const sourceName = (fd.get("sourceName") as string)?.trim() ?? "";
            const description = (fd.get("description") as string) ?? "";
            const data: Record<string, unknown> = {
              name: sourceName || extractNameFromDescription(description),
              type: "auto",
              enabled: true,
              instructions: description,
              intervalMinutes: Number(fd.get("intervalMinutes")) || 60,
            };

            if (isEdit && props.editingSource) {
              data.id = props.editingSource.id;
            }

            props.onFormSave(data as Partial<KnowledgeSourceUI>);
          }}
        >
          <!-- Source Name -->
          <div class="field" style="margin-bottom: 12px;">
            <label style="font-size: 15px; font-weight: 600; margin-bottom: 4px; display: block;">
              ${t("ks.field_name")}
            </label>
            <input
              type="text"
              name="sourceName"
              style="font-size: 14px; width: 100%;"
              placeholder=${t("ks.field_name_placeholder")}
              .value=${source.name ?? ""}
            />
          </div>

          <!-- Description -->
          <div class="field">
            <label style="font-size: 15px; font-weight: 600; margin-bottom: 4px; display: block;">
              ${t("ks.field_description")}
            </label>
            <div class="muted" style="font-size: 12px; margin-bottom: 8px; line-height: 1.5;">
              ${t("ks.field_description_hint")}
            </div>
            <textarea
              name="description"
              rows="5"
              required
              style="font-size: 14px; line-height: 1.6; resize: vertical;"
              placeholder=${t("ks.field_description_placeholder")}
            >${source.instructions ?? ""}</textarea>
          </div>

          <!-- Schedule -->
          <div style="border-top: 1px solid rgba(255,255,255,0.06); margin: 16px 0; padding-top: 16px;">
            <div class="field">
              <label>${t("ks.field_interval")}</label>
              <div class="row" style="gap: 8px; align-items: center;">
                <input type="number" name="intervalMinutes" value=${source.intervalMinutes ?? 60} min="5" max="10080" style="width: 80px;" />
                <span class="muted">${t("ks.minutes")}</span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="row" style="justify-content: flex-end; margin-top: 20px; gap: 8px;">
            <button type="button" class="btn btn-sm" @click=${props.onFormClose}>${t("ks.cancel")}</button>
            <button type="submit" class="btn btn-sm primary">${isEdit ? t("ks.save") : t("ks.create")}</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

// ─── Helpers ─────────────────────────────────────────────────

/** Extract a short name from the description (first line, truncated). */
function extractNameFromDescription(desc: string): string {
  const firstLine = desc.split("\n")[0]?.trim() ?? "";
  if (firstLine.length <= 60) return firstLine;
  return firstLine.slice(0, 57) + "...";
}

function getStatusColor(status: string): string {
  switch (status) {
    case "success":
      return "var(--success, #34d399)";
    case "error":
      return "var(--danger, #ef4444)";
    case "collecting":
      return "var(--accent, #6c8cff)";
    default:
      return "var(--text-muted, #999)";
  }
}

function formatSchedule(intervalMinutes: number, cronExpression?: string): string {
  if (cronExpression) return cronExpression;
  if (intervalMinutes >= 1440) return `${Math.floor(intervalMinutes / 1440)}d`;
  if (intervalMinutes >= 60) return `${Math.floor(intervalMinutes / 60)}h`;
  return `${intervalMinutes}m`;
}

function formatTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t("ks.just_now");
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
